import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { WebsiteService, Category, Domain, LanguageOption } from '../../services/website.service';
import { PostService } from '../../services/post.service';
import { NotificationService } from '../../services/notification.service';
import { NotificationComponent } from '../../components/notification/notification.component';

interface CategoryNode extends Category {
  children: CategoryNode[];
  postCount: number;
  editing: boolean;
  editName: string;
  editSlug: string;
  editTranslations: Record<string, string>;
  expanded: boolean;
}

@Component({
  selector: 'app-categories',
  standalone: true,
  imports: [CommonModule, FormsModule, NotificationComponent],
  templateUrl: './categories.component.html'
})
export class CategoriesComponent implements OnInit {
  categories: CategoryNode[] = [];
  domains: Domain[] = [];
  currentDomain: string | null = null;
  loading = false;
  availableLanguages: LanguageOption[] = [];

  // Nieuwe categorie
  showNewCategoryInput = false;
  newCategoryName = '';
  newCategorySlug = '';
  newCategoryParentId: string | null = null;

  // Subcategorie toevoegen
  addingSubTo: string | null = null;
  newSubCategoryName = '';

  constructor(
    private websiteService: WebsiteService,
    private postService: PostService,
    private notificationService: NotificationService,
    private router: Router
  ) {}

  async ngOnInit() {
    this.domains = await this.websiteService.getDomains();
    const current = this.websiteService.getCurrentDomain();
    this.currentDomain = current && this.domains.some(d => d.name === current)
      ? current
      : this.domains[0]?.name || null;

    if (this.currentDomain) {
      await this.loadCategories();
      const settings = await this.websiteService.getWebsiteSettings(this.currentDomain);
      this.availableLanguages = settings?.availableLanguages || [];
    }
  }

  async onDomainChange(domain: string) {
    this.currentDomain = domain;
    await this.loadCategories();
  }

  async loadCategories() {
    if (!this.currentDomain) return;
    this.loading = true;

    try {
      const [flatCategories, posts] = await Promise.all([
        this.websiteService.getCategories(this.currentDomain),
        this.postService.getAllPostsForDomain(this.currentDomain)
      ]);

      // Bereken post counts per categorie
      const countMap = new Map<string, number>();
      for (const post of posts) {
        for (const catId of (post.categoryIds || [])) {
          countMap.set(catId, (countMap.get(catId) || 0) + 1);
        }
      }

      // Bouw de boom op
      const nodeMap = new Map<string, CategoryNode>();
      for (const cat of flatCategories) {
        nodeMap.set(cat.id, {
          ...cat,
          children: [],
          postCount: countMap.get(cat.id) || 0,
          editing: false,
          editName: cat.name,
          editSlug: cat.slug || '',
          editTranslations: { ...(cat.translations || {}) },
          expanded: true
        });
      }

      this.categories = [];
      for (const node of nodeMap.values()) {
        if (node.parentId && nodeMap.has(node.parentId)) {
          nodeMap.get(node.parentId)!.children.push(node);
        } else {
          this.categories.push(node);
        }
      }

      // Sorteer
      this.sortNodes(this.categories);
    } catch (error) {
      console.error('Fout bij laden categorieën:', error);
      this.notificationService.show({ message: 'Fout bij laden categorieën', type: 'error' });
    } finally {
      this.loading = false;
    }
  }

  private sortNodes(nodes: CategoryNode[]) {
    nodes.sort((a, b) => a.name.localeCompare(b.name));
    for (const node of nodes) {
      if (node.children.length) this.sortNodes(node.children);
    }
  }

  getTotalPostCount(node: CategoryNode): number {
    let count = node.postCount;
    for (const child of node.children) {
      count += this.getTotalPostCount(child);
    }
    return count;
  }

  // CRUD
  async addCategory() {
    const name = this.newCategoryName.trim();
    if (!name || !this.currentDomain) return;

    try {
      await this.websiteService.createCategory(this.currentDomain, name, this.newCategoryParentId, this.newCategorySlug.trim() || undefined);
      this.newCategoryName = '';
      this.newCategorySlug = '';
      this.showNewCategoryInput = false;
      this.newCategoryParentId = null;
      this.notificationService.show({ message: 'Categorie toegevoegd', type: 'success' });
      await this.loadCategories();
    } catch (error) {
      this.notificationService.show({ message: 'Fout bij toevoegen', type: 'error' });
    }
  }

  async addSubCategory(parentId: string) {
    const name = this.newSubCategoryName.trim();
    if (!name || !this.currentDomain) return;

    try {
      await this.websiteService.createCategory(this.currentDomain, name, parentId);
      this.newSubCategoryName = '';
      this.addingSubTo = null;
      this.notificationService.show({ message: 'Subcategorie toegevoegd', type: 'success' });
      await this.loadCategories();
    } catch (error) {
      this.notificationService.show({ message: 'Fout bij toevoegen', type: 'error' });
    }
  }

  startEdit(node: CategoryNode) {
    node.editing = true;
    node.editName = node.name;
    node.editSlug = node.slug || '';
    node.editTranslations = { ...(node.translations || {}) };
  }

  cancelEdit(node: CategoryNode) {
    node.editing = false;
    node.editName = node.name;
  }

  async saveEdit(node: CategoryNode) {
    const name = node.editName.trim();
    if (!name || !this.currentDomain) return;

    try {
      await this.websiteService.updateCategory(
        node.id, name, this.currentDomain,
        node.editSlug.trim() || undefined,
        node.editTranslations
      );
      node.name = name;
      node.slug = node.editSlug.trim();
      node.translations = { ...node.editTranslations };
      node.editing = false;
      this.notificationService.show({ message: 'Categorie opgeslagen', type: 'success' });
    } catch (error) {
      this.notificationService.show({ message: 'Fout bij opslaan', type: 'error' });
    }
  }

  async deleteCategory(node: CategoryNode) {
    if (!this.currentDomain) return;
    const msg = node.children.length
      ? `"${node.name}" verwijderen? Subcategorieën worden naar root verplaatst.`
      : `"${node.name}" verwijderen?`;

    if (!confirm(msg)) return;

    try {
      await this.websiteService.deleteCategory(node.id, this.currentDomain);
      this.notificationService.show({ message: 'Categorie verwijderd', type: 'success' });
      await this.loadCategories();
    } catch (error) {
      this.notificationService.show({ message: 'Fout bij verwijderen', type: 'error' });
    }
  }

  startAddSub(parentId: string) {
    this.addingSubTo = parentId;
    this.newSubCategoryName = '';
  }

  cancelAddSub() {
    this.addingSubTo = null;
    this.newSubCategoryName = '';
  }

  toggleExpand(node: CategoryNode) {
    node.expanded = !node.expanded;
  }

  goBack() {
    this.router.navigate(['/posts']);
  }

  getTotalSubCount(): number {
    let count = 0;
    const countChildren = (nodes: CategoryNode[]) => {
      for (const n of nodes) {
        count += n.children.length;
        countChildren(n.children);
      }
    };
    countChildren(this.categories);
    return count;
  }

  getTotalPosts(): number {
    const seen = new Set<number>();
    let total = 0;
    const count = (nodes: CategoryNode[]) => {
      for (const n of nodes) {
        total += n.postCount;
        count(n.children);
      }
    };
    count(this.categories);
    return total;
  }
}
